import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      // Validate message length
      if (formData.message.length > 500) {
        throw new Error('Message must be 500 characters or less');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4c3746oquaeac4he10g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/f592a62f03afc4ac0d032746d48cdf2f.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Contact <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Us</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-2xl">
              Ready to transform your technology vision into reality? Get in touch with our expert team for customized solutions and strategic partnerships.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center border border-blue-400/30">
                  <i className="ri-phone-line text-blue-400 text-lg"></i>
                </div>
                <div>
                  <div className="text-sm text-white/60">Phone</div>
                  <div className="font-medium">+852 5798 7927</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center border border-blue-400/30">
                  <i className="ri-mail-line text-blue-400 text-lg"></i>
                </div>
                <div>
                  <div className="text-sm text-white/60">Email</div>
                  <div className="font-medium">info@sentronics.hk</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8 lg:p-10">
              <h2 className="text-3xl font-bold text-white mb-2">Send us a Message</h2>
              <p className="text-white/80 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-400/30 rounded-lg backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-green-300">
                    <i className="ri-check-circle-line"></i>
                    <span className="font-medium">Message sent successfully!</span>
                  </div>
                  <p className="text-green-200 text-sm mt-1">We'll get back to you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-lg backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-red-300">
                    <i className="ri-error-warning-line"></i>
                    <span className="font-medium">Failed to send message</span>
                  </div>
                  <p className="text-red-200 text-sm mt-1">Please try again or contact us directly.</p>
                </div>
              )}

              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white/90 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm text-white placeholder-white/50 backdrop-blur-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm text-white placeholder-white/50 backdrop-blur-sm"
                      placeholder="your.email@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-white/90 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm text-white placeholder-white/50 backdrop-blur-sm"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-white/90 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm text-white placeholder-white/50 backdrop-blur-sm"
                      placeholder="+852 1234 5678"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-white/90 mb-2">
                    Subject
                  </label>
                  <div className="relative">
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm appearance-none cursor-pointer text-white backdrop-blur-sm"
                    >
                      <option value="" className="bg-slate-800 text-white">Select a subject</option>
                      <option value="Technology R&D" className="bg-slate-800 text-white">Technology R&D</option>
                      <option value="OEM/ODM Services" className="bg-slate-800 text-white">OEM/ODM Services</option>
                      <option value="Technology Transfer" className="bg-slate-800 text-white">Technology Transfer</option>
                      <option value="Consulting Services" className="bg-slate-800 text-white">Consulting Services</option>
                      <option value="Partnership Inquiry" className="bg-slate-800 text-white">Partnership Inquiry</option>
                      <option value="General Inquiry" className="bg-slate-800 text-white">General Inquiry</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-white/50"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-white/90 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all text-sm resize-vertical text-white placeholder-white/50 backdrop-blur-sm"
                    placeholder="Tell us about your project requirements, technology needs, or how we can help you..."
                  ></textarea>
                  <div className="text-right text-xs text-white/60 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg shadow-blue-600/25"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending Message...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line"></i>
                      Send Message
                    </span>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Office Location */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Our Office</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0 border border-blue-400/30">
                      <i className="ri-map-pin-line text-blue-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Address</h4>
                      <p className="text-white/80 leading-relaxed">
                        Unit 89, 3/F., Yau Lee Centre<br />
                        No.45, Hoi Yuen Road, Kwun Tong<br />
                        Hong Kong
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center flex-shrink-0 border border-green-400/30">
                      <i className="ri-time-line text-green-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Business Hours</h4>
                      <div className="text-white/80 space-y-1">
                        <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                        <p>Saturday: 9:00 AM - 1:00 PM</p>
                        <p>Sunday: Closed</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-600/20 rounded-full flex items-center justify-center flex-shrink-0 border border-purple-400/30">
                      <i className="ri-customer-service-line text-purple-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Response Time</h4>
                      <p className="text-white/80">
                        We typically respond to inquiries within 24 hours during business days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-md rounded-2xl p-8 border border-blue-400/30">
                <h3 className="text-2xl font-bold text-white mb-6">Why Partner with Us?</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-white/90">20+ years of industry experience</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-white/90">Proven track record of successful projects</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-white/90">Dedicated support throughout your journey</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-white/90">Competitive pricing with quality assurance</span>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-white/20">
                  <h3 className="text-xl font-bold text-white">Find Us</h3>
                  <p className="text-white/80 mt-1">Located in the heart of Kwun Tong, Hong Kong</p>
                </div>
                <div className="h-64">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.8234567890123!2d114.2234567890123!3d22.3234567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDE5JzI0LjQiTiAxMTTCsDEzJzI0LjQiRQ!5e0!3m2!1sen!2shk!4v1234567890123!5m2!1sen!2shk"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Sentronics Office Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
